import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFeedback } from "../components/ActionFeedback";
import API from "../api/axios";
import Lottie from "lottie-react";
import noDataAnimation from "./no_data.json";
import Blogo from "../assets/Blogo.png";

const ProfileModalContent = () => {
  const [profilePic, setProfilePic] = useState(localStorage.getItem('profilePic') || "https://ui-avatars.com/api/?name=Glow+Ur+Brand&background=0a0c10&color=fff&bold=true");

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePic(event.target.result);
        localStorage.setItem('profilePic', event.target.result);
        window.dispatchEvent(new Event('profilePicUpdated'));
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-6 p-6 bg-slate-50 rounded-3xl border border-slate-100">
        <input 
          type="file" 
          id="profilePicInput" 
          className="hidden" 
          accept="image/*"
          onChange={handleImageChange}
        />
        <div 
          className="w-20 h-20 rounded-2xl overflow-hidden border border-slate-200 relative group cursor-pointer bg-white flex-shrink-0"
          onClick={() => document.getElementById('profilePicInput').click()}
        >
          <img src={profilePic} alt="Admin" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0a0c10]/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-white text-[10px] font-black uppercase tracking-widest">Upload</span>
          </div>
        </div>
        <div>
          <p className="text-xl font-black text-slate-800 tracking-tight uppercase">Glow Ur Brand</p>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Super Administrator</p>
          <button 
            type="button" 
            onClick={() => document.getElementById('profilePicInput').click()}
            className="mt-3 px-4 py-1.5 bg-white border border-slate-200 text-[10px] font-black uppercase text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            Change Photo
          </button>
        </div>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Full Name</label>
          <input type="text" defaultValue="GlowUrBrand" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 text-sm font-bold text-slate-800 outline-none focus:bg-white focus:border-[#0a0c10] transition-colors" required />
        </div>
        <div>
          <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Email Address</label>
          <input type="email" defaultValue="admin@glowurbrand.com" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 text-sm font-bold text-slate-800 outline-none focus:bg-white focus:border-[#0a0c10] transition-colors" required />
        </div>
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const location = useLocation();
  const { showToast, showModal, closeModal } = useFeedback();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [headerPic, setHeaderPic] = useState(localStorage.getItem('profilePic') || "https://ui-avatars.com/api/?name=Glow+Ur+Brand&background=0a0c10&color=fff&bold=true");

  useEffect(() => {
    const updatePic = () => setHeaderPic(localStorage.getItem('profilePic') || "https://ui-avatars.com/api/?name=Glow+Ur+Brand&background=0a0c10&color=fff&bold=true");
    window.addEventListener('profilePicUpdated', updatePic);
    return () => window.removeEventListener('profilePicUpdated', updatePic);
  }, []);

  const navItems = [
    { name: "Overview", path: "/dashboard" },
    { name: "Events", path: "/events" },
    { name: "Registrations", path: "/registrations" },
    { name: "Users", path: "/users" },
    { name: "Settings", path: "/settings" },
  ];

  const [notifications, setNotifications] = useState([]);
  const [lastRead, setLastRead] = useState(() => {
    const stored = localStorage.getItem("notificationsLastRead");
    return stored ? new Date(stored) : new Date(0);
  });

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await API.get("/registrations");
        const recentRegs = res.data
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
          .map(reg => ({
            id: reg._id,
            title: "New Registration",
            message: `${reg.user_id?.name || "Someone"} registered for ${reg.event_id}`,
            time: new Date(reg.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            createdAt: new Date(reg.createdAt),
          }));
        setNotifications(recentRegs);
      } catch (err) {
        console.error("Failed to fetch notifications");
      }
    };
    fetchNotifications();
  }, []);

  const unreadCount = notifications.filter(n => n.createdAt > lastRead).length;

  const handleNotificationsClick = () => {
    // Mark all as read — persist the timestamp
    const now = new Date();
    setLastRead(now);
    localStorage.setItem("notificationsLastRead", now.toISOString());

    showModal({
      title: "Recent Alerts",
      subtitle: "System notifications and updates",
      content: (
        <div className="space-y-4">
          {notifications.length > 0 ? notifications.map((notif, i) => (
            <div key={notif.id || i} className="p-4 bg-slate-50 text-slate-800 rounded-xl border border-slate-100 flex justify-between items-center">
              <div>
                <p className="text-sm font-black uppercase">{notif.title}</p>
                <p className="text-[10px] font-bold text-slate-500 mt-1">{notif.message}</p>
              </div>
              <span className="text-xs font-bold text-slate-400">{notif.time}</span>
            </div>
          )) : (
            <div className="p-4 flex flex-col items-center justify-center">
              <div className="w-24 h-24 mb-2 opacity-70">
                <Lottie animationData={noDataAnimation} loop={true} />
              </div>
              <span className="text-center text-slate-400 font-bold text-xs uppercase">No recent alerts</span>
            </div>
          )}
        </div>
      ),
    });
  };

  const handleLogout = () => {
    setIsProfileOpen(false);
    showModal({
      title: "Confirm Logout",
      subtitle: "Secure Session End",
      content: (
        <div className="py-6 text-center space-y-4">
          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-4xl mx-auto border border-slate-100 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-slate-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          </div>
          <p className="text-sm font-black text-slate-800">Are you sure you want to end your session?</p>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">You will need to re-authenticate to access the portal again.</p>
        </div>
      ),
      confirmText: "Secure Logout",
      cancelText: "Keep Session",
      onConfirm: () => {
        showToast("Logging out of system...", "info");
        setTimeout(() => {
          localStorage.removeItem("token");
          window.location.reload();
        }, 1000);
      }
    });
  };

  const handleMyProfile = () => {
    showModal({
      title: "My Profile",
      subtitle: "Update your personal details",
      content: <ProfileModalContent />,
      confirmText: "Save Changes",
      cancelText: "Cancel",
      onConfirm: () => {
        showToast("Profile updated successfully.", "success");
        setIsProfileOpen(false);
      }
    });
  };

  return (
    <div className="puzzle-container">
      <header className="h-24 px-12 flex items-center justify-between text-white sticky top-0 bg-[#0a0c10] z-50">
        <div className="flex items-center gap-12">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src={Blogo} alt="Logo" className="w-8 h-8 object-contain" />
            <span className="font-black text-xl tracking-tighter uppercase">Glow Ur Brand</span>
          </Link>

          {/* Nav */}
          <nav className="flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-bold transition-all ${
                  location.pathname === item.path ? "text-white" : "text-slate-500 hover:text-white"
                } relative py-2`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>
                )}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-6">
          {/* <div className="relative hidden md:block group">
            <input 
              type="text" 
              placeholder="Quick search..." 
              className="w-48 bg-white/5 border border-white/10 rounded-full py-1.5 px-4 pl-10 text-xs focus:w-64 focus:bg-white/10 transition-all font-bold outline-none text-white placeholder:text-slate-400"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1.5 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" /></svg>
          </div> */}
          
          <button onClick={handleNotificationsClick} className="text-slate-500 hover:text-white transition-colors transform hover:scale-110 relative">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
            {unreadCount > 0 && (
              <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full animate-pulse flex items-center justify-center text-[8px] text-white font-bold"></div>
            )}
          </button>
          
          <div className="relative">
            <div 
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-4 bg-white/5 border border-white/10 pl-2 pr-4 py-1.5 rounded-full hover:bg-white/10 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20 relative bg-white/10 p-0.5">
                <img src={headerPic} alt="Admin" className="w-full h-full object-cover rounded-full" />
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-black text-white">GlowUrBrand</p>
                <p className="text-[10px] text-slate-500 font-bold">Admin</p>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-slate-600 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
            </div>

            {isProfileOpen && (
              <div className="absolute right-0 mt-4 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 py-2 animate-fade-in z-50">
                <div className="px-5 py-3 border-b border-slate-50 text-left">
                  <p className="text-sm font-black text-slate-800">GlowUrBrand</p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">admin@glowurbrand.com</p>
                </div>
                <div className="py-2 text-left">
                  <button 
                    onClick={() => {
                      setIsProfileOpen(false);
                      handleMyProfile();
                    }}
                    className="w-full text-left px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-[#0a0c10] hover:bg-slate-50 uppercase tracking-widest transition-colors"
                  >
                    My Profile
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-5 py-2.5 text-xs font-bold text-red-600 hover:text-red-700 hover:bg-red-50 uppercase tracking-widest transition-colors"
                  >
                    Secure Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 px-12 py-8 overflow-y-auto animate-slide-up">
        {children}
      </main>
    </div>
  );
}